/*
 * IndexedDB Blob store for local mode. Image and video bytes live here so
 * localStorage stays under its ~5 MB quota.
 */

import { openDB, type IDBPDatabase } from 'idb';

const DB_NAME = 'studio-v2-local-media';
const DB_VERSION = 1;
const STORE = 'blobs';

let dbPromise: Promise<IDBPDatabase> | null = null;
function db(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(database) {
        if (!database.objectStoreNames.contains(STORE)) {
          database.createObjectStore(STORE);
        }
      },
    });
  }
  return dbPromise;
}

const urlCache = new Map<string, string>();

export async function putBlob(id: string, blob: Blob): Promise<string> {
  const database = await db();
  await database.put(STORE, blob, id);
  return blobPath(id);
}

export function blobPath(id: string): string {
  return `idb://local-media/${id}`;
}

export async function resolveBlobUrl(path: string): Promise<string | null> {
  if (!path.startsWith('idb://local-media/')) return null;
  const id = path.slice('idb://local-media/'.length);
  const cached = urlCache.get(id);
  if (cached) return cached;
  const database = await db();
  const blob = (await database.get(STORE, id)) as Blob | undefined;
  if (!blob) return null;
  const url = URL.createObjectURL(blob);
  urlCache.set(id, url);
  return url;
}

export async function deleteBlob(id: string): Promise<void> {
  const database = await db();
  await database.delete(STORE, id);
  const cached = urlCache.get(id);
  if (cached) URL.revokeObjectURL(cached);
  urlCache.delete(id);
}

export async function clearAllBlobs(): Promise<void> {
  const database = await db();
  await database.clear(STORE);
  for (const url of urlCache.values()) URL.revokeObjectURL(url);
  urlCache.clear();
}
